import { useEffect, useState } from "react";
import { CUSTOMIZABLE_SHORTCUT_ACTIONS, type ShortcutAction } from "../types";
import { comboFromEvent, DEFAULT_SHORTCUTS, effectiveShortcut, formatCombo, SHORTCUT_LABEL } from "../utils/shortcuts";

interface Props {
  onClose: () => void;
  shortcuts: Partial<Record<ShortcutAction, string>>;
  onSaveShortcuts: (patch: Partial<Record<ShortcutAction, string>>) => void;
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const mod = isMac ? "⌘" : "Ctrl";

const FIXED_SHORTCUTS: { keys: string[]; desc: string }[] = [
  { keys: ["Esc"], desc: "Parar a resposta em andamento" },
  { keys: ["Enter"], desc: "Enviar mensagem" },
  { keys: ["Shift", "Enter"], desc: "Quebra de linha na mensagem" },
];

export function ShortcutsModal({ onClose, shortcuts, onSaveShortcuts }: Props) {
  const [capturing, setCapturing] = useState<ShortcutAction | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (capturing) {
        const combo = comboFromEvent(e);
        if (!combo) return; // só modificador apertado ainda, espera a tecla de verdade
        e.preventDefault();
        e.stopPropagation();
        onSaveShortcuts({ [capturing]: combo });
        setCapturing(null);
        return;
      }
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [capturing, onClose, onSaveShortcuts]);

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Atalhos de teclado"
      >
        <header className="modal-header">
          <h2>Atalhos de teclado</h2>
          <button className="modal-close" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>
        <ul className="shortcuts-list">
          {CUSTOMIZABLE_SHORTCUT_ACTIONS.map((action) => {
            const combo = effectiveShortcut(shortcuts, action);
            const isCustom = Boolean(shortcuts[action]);
            return (
              <li key={action}>
                <span className="shortcut-desc">{SHORTCUT_LABEL[action]}</span>
                <span className="shortcut-edit">
                  {capturing === action ? (
                    <span className="shortcut-capturing">Pressione a nova combinação…</span>
                  ) : (
                    <>
                      <kbd>{formatCombo(combo)}</kbd>
                      <button type="button" className="shortcut-remap-btn" onClick={() => setCapturing(action)}>
                        Alterar
                      </button>
                      {isCustom && (
                        <button
                          type="button"
                          className="shortcut-remap-btn"
                          onClick={() => onSaveShortcuts({ [action]: DEFAULT_SHORTCUTS[action] })}
                        >
                          Restaurar
                        </button>
                      )}
                    </>
                  )}
                </span>
              </li>
            );
          })}
          {FIXED_SHORTCUTS.map((s) => (
            <li key={s.desc}>
              <span className="shortcut-desc">{s.desc}</span>
              <span className="shortcut-keys">
                {s.keys.map((k) => (
                  <kbd key={k}>{k}</kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <p className="shortcut-hint">{mod}+K e {mod}+J são os padrões - clique em "Alterar" pra usar outra combinação.</p>
      </div>
    </div>
  );
}
